import classNames from 'classnames';
import type { Placement } from 'shared/ui/Tooltip';
import { Tooltip } from 'shared/ui/Tooltip';
import { formatDecimal, formatPrice, formatToCents } from 'shared/lib/formatting';

export interface PercentageCellProps {
  className?: string;
  total: string | number;
  percent?: string | number;
  title: string | number;
  isMoney?: boolean;
  onClick?: () => void;
  isFormatToCents?: boolean;
  isFormat?: boolean;
  isPercentageBadge?: boolean;
  isPercentageBadgeTooltip?: boolean;
  percentageBadgeTooltipPlacement?: Placement;
  percentageBadgeTooltipContent?: string | JSX.Element;
  percentageTooltipClass?: string;
  isTotalBadge?: boolean;
  isTotalBadgeTooltip?: boolean;
  totalBadgeTooltipPlacement?: Placement;
  totalBadgeTooltipContent?: string | JSX.Element;
  totalTooltipClass?: string;
}

export function PercentageCell({
  className,
  total,
  title,
  percent,
  isMoney,
  isFormatToCents,
  onClick,
  percentageTooltipClass = 'max-w-[273px]',
  totalTooltipClass = 'max-w-[273px]',
  isFormat,
  isPercentageBadge,
  isPercentageBadgeTooltip,
  percentageBadgeTooltipPlacement = 'top-start',
  percentageBadgeTooltipContent,
  isTotalBadge,
  isTotalBadgeTooltip,
  totalBadgeTooltipPlacement = 'bottom-end',
  totalBadgeTooltipContent,
}: PercentageCellProps) {
  const showText = (data: string | number) => {
    if (isFormatToCents) {
      return formatToCents(data);
    }
    if (isMoney) {
      return formatPrice(Number(data));
    }

    if (isFormat) {
      return formatDecimal(data);
    }

    return data;
  };

  return (
    <td
      className={className}
      onClick={onClick}
    >
      <div className="text-right text-[13px] font-medium leading-4 text-black">{showText(title)}</div>
      <div className="flex items-center justify-end text-right text-[10px] font-medium leading-5 text-slate-600">
        <Tooltip
          placement={percentageBadgeTooltipPlacement}
          content={<div className="-ml-1 text-left">{percentageBadgeTooltipContent}</div>}
          className={classNames({
            'cursor-pointer': isPercentageBadgeTooltip,
            'mt-[1px] mr-[4px]': isPercentageBadge,
            'mr-[6px]': !isPercentageBadge,
          })}
          contentClass={percentageTooltipClass}
          isDisabled={!isPercentageBadgeTooltip}
        >
          {isPercentageBadge ? (
            <div className="relative h-[16px] overflow-hidden rounded-lg bg-magenta-50 px-[6px]">
              <div className="text-[9px] font-semibold leading-4 text-magenta-700">{percent}%</div>
            </div>
          ) : (
            <div className="mr-[-3px]">{percent}%</div>
          )}
        </Tooltip>
        <div className="mr-[4px]">of </div>
        <Tooltip
          placement={totalBadgeTooltipPlacement}
          content={<div className="-ml-1 text-left">{totalBadgeTooltipContent}</div>}
          className={classNames({ 'cursor-pointer': isTotalBadgeTooltip, 'mt-[1px]': isTotalBadge === true })}
          contentClass={totalTooltipClass}
          isDisabled={!isTotalBadgeTooltip}
        >
          {isTotalBadge ? (
            <div className="relative h-[16px] overflow-hidden rounded-lg bg-slate-100 px-[6px]">
              <div className="text-[9px] font-semibold leading-4 text-slate-900">{showText(total)}</div>
            </div>
          ) : (
            <div>{showText(total)}</div>
          )}
        </Tooltip>
      </div>
    </td>
  );
}
